import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"

const SIGNATURE_WIDTH = 800
const SIGNATURE_HEIGHT = 200
const MAX_FILE_SIZE = 5 * 1024 * 1024
const ACCEPT_TYPES = ["image/png", "image/jpeg", "image/webp"]

export default class extends Controller {
  static targets = [
    "modal", "canvas", "typeCanvas", "saveButton", "errorMessage", "savingState",
    "preview", "emptyState", "drawPanel", "typePanel", "drawTab", "typeTab",
    "signatureText", "fontOption",
    "uploadTab", "uploadPanel", "uploadFileInput", "uploadPickerArea",
    "uploadCropArea", "uploadCropImage", "field", "fieldPreview", "fieldButton"
  ]
  // fieldMode: instead of PATCHing the profile endpoint, hand the signature to
  // a hidden `field` (a base64 PNG data URL) and preview it in `fieldPreview`,
  // for a form that saves the signature with the rest of its record - a named
  // person's on a certification (TASK-283.7).
  // autoOpen: open the draw pad as soon as the controller connects - for a
  // signing modal whose member has no saved signature yet (TASK-292.3).
  static values = { updateUrl: String, csrfToken: String, selectedFont: String, fieldMode: Boolean, autoOpen: Boolean, tickSelector: String }

  connect() {
    this.isDrawing = false
    this.lastX = 0
    this.lastY = 0
    this.activeMode = "draw"
    this.selectedFontValue = "Caveat"
    this.uploadCropper = null
    this.uploadObjectUrl = null

    // Canvas text paints with whatever is loaded at draw time — if the
    // handwriting webfonts arrive after the first preview, it stays in
    // fallback cursive. Repaint once the FontFaceSet settles (TASK-101.1).
    if (this.autoOpenValue) requestAnimationFrame(() => this.openModal())

    document.fonts?.ready?.then(() => {
      if (this.activeMode === "type") this.renderTextToCanvas()
    })
  }

  // Browsers fetch a webfont on first use, which can be after the canvas has
  // already drawn with the fallback. Kick off an explicit load for the chosen
  // face and repaint when it lands; no-ops once the font is resident.
  _repaintWhenFontLoads(font) {
    if (!document.fonts?.load || this._pendingFontLoad === font) return
    if (document.fonts.check(`32px "${font}"`)) return

    this._pendingFontLoad = font
    document.fonts.load(`32px "${font}"`).then(() => {
      this._pendingFontLoad = null
      if (this.activeMode === "type" && (this.selectedFontValue || "Caveat") === font) {
        this.renderTextToCanvas()
      }
    }).catch(() => { this._pendingFontLoad = null })
  }

  disconnect() {
    this._cleanupUploadCropper()
  }

  openModal() {
    this.modalTarget.showModal()
    this.errorMessageTarget.classList.add("hidden")
    this.activeMode = "draw"
    this._activateDrawTab()
    requestAnimationFrame(() => this.initCanvas())
  }

  closeModal() {
    this._cleanupUploadCropper()
    this.modalTarget.close()
  }

  backdropClose(event) {
    if (event.target === event.currentTarget) {
      this.closeModal()
    }
  }

  // ─── Tab switching ─────────────────────────────────────────────────────────

  switchToDrawTab() {
    this.activeMode = "draw"
    this._activateDrawTab()
    this.clearCanvas()
    requestAnimationFrame(() => this.initCanvas())
  }

  switchToTypeTab() {
    this.activeMode = "type"
    this._activateTypeTab()
    this._removeDrawListeners()
    this._cleanupUploadCropper()
    requestAnimationFrame(() => {
      this._initTypeCanvas()
      this.renderTextToCanvas()
    })
  }

  switchToUploadTab() {
    this.activeMode = "upload"
    this._activateUploadTab()
    this._removeDrawListeners()
  }

  _activateDrawTab() {
    this.drawPanelTarget.classList.remove("hidden")
    this.typePanelTarget.classList.add("hidden")
    if (this.hasUploadPanelTarget) this.uploadPanelTarget.classList.add("hidden")
    this._setTabActive(this.drawTabTarget)
    this._setTabInactive(this.typeTabTarget)
    if (this.hasUploadTabTarget) this._setTabInactive(this.uploadTabTarget)
  }

  _activateTypeTab() {
    this.typePanelTarget.classList.remove("hidden")
    this.drawPanelTarget.classList.add("hidden")
    if (this.hasUploadPanelTarget) this.uploadPanelTarget.classList.add("hidden")
    this._setTabActive(this.typeTabTarget)
    this._setTabInactive(this.drawTabTarget)
    if (this.hasUploadTabTarget) this._setTabInactive(this.uploadTabTarget)
  }

  _activateUploadTab() {
    if (this.hasUploadPanelTarget) this.uploadPanelTarget.classList.remove("hidden")
    this.drawPanelTarget.classList.add("hidden")
    this.typePanelTarget.classList.add("hidden")
    if (this.hasUploadTabTarget) this._setTabActive(this.uploadTabTarget)
    this._setTabInactive(this.drawTabTarget)
    this._setTabInactive(this.typeTabTarget)
  }

  _setTabActive(tab) {
    tab.classList.add("text-blue-600", "border-blue-600")
    tab.classList.remove("text-gray-500", "border-transparent")
  }

  _setTabInactive(tab) {
    tab.classList.remove("text-blue-600", "border-blue-600")
    tab.classList.add("text-gray-500", "border-transparent")
  }

  // ─── Draw mode canvas ──────────────────────────────────────────────────────

  initCanvas() {
    this.canvas = this.canvasTarget
    this.ctx = this.canvas.getContext("2d")

    this.ctx.strokeStyle = "#000000"
    this.ctx.lineWidth = 2.5
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"

    this.resizeCanvas()

    this._removeDrawListeners()

    this._startDrawing = this.startDrawing.bind(this)
    this._draw = this.draw.bind(this)
    this._stopDrawing = this.stopDrawing.bind(this)
    this._touchStart = this.handleTouchStart.bind(this)
    this._touchMove = this.handleTouchMove.bind(this)

    this.canvas.addEventListener("mousedown", this._startDrawing)
    this.canvas.addEventListener("mousemove", this._draw)
    this.canvas.addEventListener("mouseup", this._stopDrawing)
    this.canvas.addEventListener("mouseout", this._stopDrawing)
    this.canvas.addEventListener("touchstart", this._touchStart, { passive: false })
    this.canvas.addEventListener("touchmove", this._touchMove, { passive: false })
    this.canvas.addEventListener("touchend", this._stopDrawing)
  }

  _removeDrawListeners() {
    if (!this.canvas) return
    if (this._startDrawing) this.canvas.removeEventListener("mousedown", this._startDrawing)
    if (this._draw) this.canvas.removeEventListener("mousemove", this._draw)
    if (this._stopDrawing) {
      this.canvas.removeEventListener("mouseup", this._stopDrawing)
      this.canvas.removeEventListener("mouseout", this._stopDrawing)
      this.canvas.removeEventListener("touchend", this._stopDrawing)
    }
    if (this._touchStart) this.canvas.removeEventListener("touchstart", this._touchStart)
    if (this._touchMove) this.canvas.removeEventListener("touchmove", this._touchMove)
  }

  resizeCanvas() {
    const rect = this.canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1

    this.canvas.width = rect.width * dpr
    this.canvas.height = rect.height * dpr

    this.ctx.scale(dpr, dpr)
    this.ctx.strokeStyle = "#000000"
    this.ctx.lineWidth = 2.5
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"
  }

  getCoordinates(event) {
    const rect = this.canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    return {
      x: (event.clientX - rect.left) * (this.canvas.width / rect.width) / dpr,
      y: (event.clientY - rect.top) * (this.canvas.height / rect.height) / dpr
    }
  }

  startDrawing(event) {
    this.isDrawing = true
    const coords = this.getCoordinates(event)
    this.lastX = coords.x
    this.lastY = coords.y
  }

  draw(event) {
    if (!this.isDrawing) return
    const coords = this.getCoordinates(event)

    this.ctx.beginPath()
    this.ctx.moveTo(this.lastX, this.lastY)
    this.ctx.lineTo(coords.x, coords.y)
    this.ctx.stroke()

    this.lastX = coords.x
    this.lastY = coords.y
  }

  stopDrawing() {
    this.isDrawing = false
  }

  handleTouchStart(event) {
    event.preventDefault()
    const touch = event.touches[0]
    this.startDrawing(new MouseEvent("mousedown", { clientX: touch.clientX, clientY: touch.clientY }))
  }

  handleTouchMove(event) {
    event.preventDefault()
    const touch = event.touches[0]
    this.draw(new MouseEvent("mousemove", { clientX: touch.clientX, clientY: touch.clientY }))
  }

  clearCanvas() {
    if (this.ctx) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    }
  }

  isCanvasEmpty() {
    if (!this.ctx) return true
    const data = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height).data
    return !Array.from(data).some((val, idx) => idx % 4 === 3 && val !== 0)
  }

  // ─── Type mode canvas ──────────────────────────────────────────────────────

  _initTypeCanvas() {
    this.typeCtx = this.typeCanvasTarget.getContext("2d")
    const rect = this.typeCanvasTarget.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    this.typeCanvasTarget.width = rect.width * dpr
    this.typeCanvasTarget.height = rect.height * dpr
    this.typeCtx.scale(dpr, dpr)
  }

  selectFont(event) {
    const font = event.currentTarget.dataset.font
    this.selectedFontValue = font

    this.fontOptionTargets.forEach(btn => {
      const isSelected = btn.dataset.font === font
      btn.classList.toggle("border-blue-500", isSelected)
      btn.classList.toggle("bg-blue-50", isSelected)
      btn.classList.toggle("border-gray-200", !isSelected)
    })

    this.renderTextToCanvas()
  }

  renderTextToCanvas() {
    if (!this.hasTypeCanvasTarget) return
    this._repaintWhenFontLoads(this.selectedFontValue || "Caveat")

    if (!this.typeCtx) {
      this._initTypeCanvas()
    }

    const canvas = this.typeCanvasTarget
    const ctx = this.typeCtx
    const dpr = window.devicePixelRatio || 1
    const cssWidth = canvas.width / dpr
    const cssHeight = canvas.height / dpr

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const text = this.hasSignatureTextTarget ? this.signatureTextTarget.value.trim() : ""
    if (!text) return

    const font = this.selectedFontValue || "Caveat"
    const maxFontSize = 64
    const minFontSize = 18
    const padding = 24

    let fontSize = maxFontSize
    ctx.font = `${fontSize}px '${font}', cursive`

    while (ctx.measureText(text).width > cssWidth - padding * 2 && fontSize > minFontSize) {
      fontSize -= 2
      ctx.font = `${fontSize}px '${font}', cursive`
    }

    ctx.fillStyle = "#1e3a5f"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(text, cssWidth / 2, cssHeight / 2)
  }

  _renderTextToOffscreenCanvas() {
    const offscreen = document.createElement("canvas")
    offscreen.width = SIGNATURE_WIDTH
    offscreen.height = SIGNATURE_HEIGHT
    const ctx = offscreen.getContext("2d")

    const text = this.hasSignatureTextTarget ? this.signatureTextTarget.value.trim() : ""
    if (!text) return null

    const font = this.selectedFontValue || "Caveat"
    const maxFontSize = 96
    const minFontSize = 24
    const padding = 40

    let fontSize = maxFontSize
    ctx.font = `${fontSize}px '${font}', cursive`

    while (ctx.measureText(text).width > offscreen.width - padding * 2 && fontSize > minFontSize) {
      fontSize -= 2
      ctx.font = `${fontSize}px '${font}', cursive`
    }

    ctx.fillStyle = "#1e3a5f"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(text, offscreen.width / 2, offscreen.height / 2)

    return offscreen
  }

  // ─── Upload mode ───────────────────────────────────────────────────────────

  handleUploadFile(event) {
    const file = event.target.files?.[0]
    if (!file) return

    if (!ACCEPT_TYPES.includes(file.type)) {
      this.showError("Please choose a PNG, JPEG or WebP image.")
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      this.showError("Image must be less than 5 MB.")
      return
    }
    this.hideError()

    this._cleanupUploadCropper()
    this.uploadObjectUrl = URL.createObjectURL(file)
    this.uploadCropImageTarget.src = this.uploadObjectUrl

    this.uploadCropImageTarget.onload = () => {
      this.uploadPickerAreaTarget.classList.add("hidden")
      this.uploadCropAreaTarget.classList.remove("hidden")

      this.uploadCropper = new Cropper(this.uploadCropImageTarget, {
        aspectRatio: SIGNATURE_WIDTH / SIGNATURE_HEIGHT,
        viewMode: 0,
        dragMode: "move",
        autoCropArea: 0.8,
        restore: false,
        guides: false,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        minContainerHeight: 200,
        toggleDragModeOnDblclick: false,
        background: false
      })
    }
  }

  _cleanupUploadCropper() {
    if (this.uploadCropper) {
      this.uploadCropper.destroy()
      this.uploadCropper = null
    }
    if (this.uploadObjectUrl) {
      URL.revokeObjectURL(this.uploadObjectUrl)
      this.uploadObjectUrl = null
    }
    // Reset to picker view
    if (this.hasUploadPickerAreaTarget) {
      this.uploadPickerAreaTarget.classList.remove("hidden")
    }
    if (this.hasUploadCropAreaTarget) {
      this.uploadCropAreaTarget.classList.add("hidden")
    }
    if (this.hasUploadFileInputTarget) {
      this.uploadFileInputTarget.value = ""
    }
  }

  // ─── Canvas trimming ───────────────────────────────────────────────────────

  _trimCanvas(sourceCanvas, padding = 12) {
    const ctx = sourceCanvas.getContext("2d")
    const { width, height } = sourceCanvas
    const data = ctx.getImageData(0, 0, width, height).data

    let minX = width, minY = height, maxX = 0, maxY = 0

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3]
        if (alpha > 0) {
          if (x < minX) minX = x
          if (x > maxX) maxX = x
          if (y < minY) minY = y
          if (y > maxY) maxY = y
        }
      }
    }

    // No ink found — return original canvas unchanged
    if (maxX < minX) return sourceCanvas

    const paddedMinX = Math.max(0, minX - padding)
    const paddedMinY = Math.max(0, minY - padding)
    const paddedMaxX = Math.min(width - 1, maxX + padding)
    const paddedMaxY = Math.min(height - 1, maxY + padding)
    const trimmedWidth = paddedMaxX - paddedMinX + 1
    const trimmedHeight = paddedMaxY - paddedMinY + 1

    const trimmed = document.createElement("canvas")
    trimmed.width = trimmedWidth
    trimmed.height = trimmedHeight
    trimmed.getContext("2d").drawImage(
      sourceCanvas,
      paddedMinX, paddedMinY, trimmedWidth, trimmedHeight,
      0, 0, trimmedWidth, trimmedHeight
    )
    return trimmed
  }

  // ─── Save ──────────────────────────────────────────────────────────────────

  _applyToField(dataUrl) {
    if (this.hasFieldTarget) this.fieldTarget.value = dataUrl
    if (this.hasFieldPreviewTarget) {
      this.fieldPreviewTarget.src = dataUrl
      this.fieldPreviewTarget.classList.remove("hidden")
    }
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    if (this.hasFieldButtonTarget) this.fieldButtonTarget.textContent = "Draw a New One"
    // Saving a signature is the signer saying "this is me": tick the checkbox
    // the form pointed us at (Mark as Delivered's "Sign as the trainer"), and
    // tell anyone else who is listening.
    if (this.hasTickSelectorValue && this.tickSelectorValue) {
      const box = document.querySelector(this.tickSelectorValue)
      if (box && !box.checked) {
        box.checked = true
        box.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }
    this.dispatch("saved", { detail: { dataUrl } })
    this.closeModal()
  }

  async save() {
    this.errorMessageTarget.classList.add("hidden")

    let dataUrl

    if (this.activeMode === "upload") {
      if (!this.uploadCropper) {
        this.showError("Please select an image first.")
        return
      }
      const cropped = this.uploadCropper.getCroppedCanvas({
        width: SIGNATURE_WIDTH,
        height: SIGNATURE_HEIGHT
      })
      if (!cropped) {
        this.showError("Could not crop the image. Please try again.")
        return
      }
      dataUrl = cropped.toDataURL("image/png")
    } else if (this.activeMode === "type") {
      const text = this.hasSignatureTextTarget ? this.signatureTextTarget.value.trim() : ""
      if (!text) {
        this.showError("Please type your name before saving.")
        return
      }
      const offscreen = this._renderTextToOffscreenCanvas()
      if (!offscreen) {
        this.showError("Please type your name before saving.")
        return
      }
      dataUrl = this._trimCanvas(offscreen).toDataURL("image/png")
    } else {
      if (this.isCanvasEmpty()) {
        this.showError("Please draw your signature before saving.")
        return
      }
      dataUrl = this._trimCanvas(this.canvas).toDataURL("image/png")
    }

    if (this.fieldModeValue) {
      this._applyToField(dataUrl)
      return
    }

    this.saveButtonTarget.disabled = true
    this.savingStateTarget.classList.remove("hidden")

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "text/vnd.turbo-stream.html, application/json"
        },
        body: JSON.stringify({ signature_data_url: dataUrl })
      })

      if (!response.ok) {
        throw new Error("Failed to save signature")
      }

      const contentType = response.headers.get("content-type") || ""

      if (contentType.includes("turbo-stream")) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        window.location.reload()
      }

      this.closeModal()
    } catch (error) {
      this.showError("Could not save your signature. Please try again.")
    } finally {
      this.saveButtonTarget.disabled = false
      this.savingStateTarget.classList.add("hidden")
    }
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorMessageTarget.textContent = ""
    this.errorMessageTarget.classList.add("hidden")
  }
}
